import request from '@/http/request'

// Coze工作流配置
const WORKFLOW_ID = '7569502153636184073'
const COZE_API_URL = 'https://api.coze.cn/v1/workflow/run'
const COZE_TOKEN_URL = '/ai/coze/get-online'

/**
 * 调用Coze工作流分析论文
 * @param {Array} paperIds - 论文ID数组（前10个）
 * @returns {Promise} 工作流结果
 */
export async function analyzePapersWithCoze(paperIds) {
  try {
    // 获取论文详情并提取链接
    const paperLinks = await getPaperLinks(paperIds.slice(0, 10))
    
    if (paperLinks.length === 0) {
      throw new Error('未获取到有效的论文链接')
    }
    
    // 准备工作流输入
    const content = paperLinks.join(',')
    
    // 调用Coze工作流
    const result = await callCozeWorkflow(content)
    
    // 解析分析结果
    const parsedResult = parseAnalysisResult(result)
    
    return parsedResult
    
  } catch (error) {
    console.error('论文分析失败:', error)
    throw error
  }
}

/**
 * 解析工作流分析结果
 * @param {Object} result - 工作流返回的原始结果
 * @returns {Object} 解析后的结构化结果
 */
function parseAnalysisResult(result) {
  try {
    // 如果result是字符串，先解析为JSON对象
    let parsedResult = result
    if (typeof result === 'string') {
      try {
        parsedResult = JSON.parse(result)
      } catch (e) {
        return {
          success: false,
          message: 'JSON解析失败',
          data: null
        }
      }
    }
    
    // 工作流返回的结果通常在 output2 字段中
    const analysisText = parsedResult.output2 || parsedResult.output1 || parsedResult.output || ''
    
    if (!analysisText) {
      return {
        success: false,
        message: '未获取到分析结果',
        data: null
      }
    }

    // 按照 ### 分割不同的部分
    const sections = analysisText.split('###').filter(section => section.trim())
    const parsedData = {}

    sections.forEach((section) => {
      const lines = section.trim().split('\n')
      const title = lines[0].trim()
      const content = lines.slice(1).join('\n').trim()
      
      if (title && content) {
        parsedData[title] = content
      }
    })
    
    return {
      success: true,
      message: '分析完成',
      data: {
        rawText: analysisText,
        sections: parsedData,
        summary: {
          teamCount: (parsedData['团队'] || '').split(/\d+\./).length - 1,
          achievementCount: (parsedData['成果'] || '').split(/\d+\./).length - 1,
          theoryCount: (parsedData['理论'] || '').split(/\d+\./).length - 1,
          problemCount: (parsedData['问题'] || '').split(/\d+\./).length - 1
        }
      }
    }
  } catch (error) {
    return {
      success: false,
      message: '解析分析结果失败',
      data: null,
      error: error.message
    }
  }
}

/**
 * 获取论文链接
 * @param {Array} paperIds - 论文ID数组
 * @returns {Promise<Array>} 论文链接数组
 */
async function getPaperLinks(paperIds) {
  const links = []
  
  for (const paperId of paperIds) {
    try {
      const response = await request('/api/Thesis/getThesisInfo', {
        thesis_id: paperId
      })
      
      if (response && Array.isArray(response) && response.length > 0) {
        const paperInfo = response[0]
        if (paperInfo.thesis_link) {
          links.push(paperInfo.thesis_link)
        }
      }
    } catch (error) {
      continue
    }
  }
  
  return links
}

/**
 * 获取Coze Token
 * @returns {Promise<string>} token
 */
async function getCozeToken() {
  try {
    const authHeader = localStorage.getItem('authorization')
    
    const response = await fetch(COZE_TOKEN_URL, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader
      }
    })
    
    if (!response.ok) {
      throw new Error(`获取token失败: ${response.status} ${response.statusText}`)
    }
    
    const data = await response.json()
    
    if (data && data.success && data.data && data.data.access_token) {
      return data.data.access_token
    } else {
      throw new Error('获取token失败: ' + JSON.stringify(data))
    }
  } catch (error) {
    throw error
  }
}

/**
 * 调用Coze工作流
 * @param {string} content - 输入内容
 * @returns {Promise} 工作流结果
 */
async function callCozeWorkflow(content, retryCount = 0) {
  try {
    const token = await getCozeToken()
    
    const requestData = {
      workflow_id: WORKFLOW_ID,
      parameters: {
        content: content,
        input: "111"
      }
    }
    
    const response = await fetch(COZE_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(requestData)
    })
    
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Coze API请求失败: ${response.status} ${response.statusText}, 详情: ${errorText}`)
    }
    
    const data = await response.json()
    
    if (data.code === 0 && data.data) {
      return data.data
    } else {
      const errorMsg = data.msg || data.message || '工作流执行失败'
      const errorCode = data.code || '未知错误码'
      throw new Error(`调用工作流失败 (code: ${errorCode}): ${errorMsg}`)
    }
    
  } catch (error) {
    // 系统异常重试机制
    const maxRetries = 2
    if (error.message.includes('5000') && retryCount < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, 5000))
      return await callCozeWorkflow(content, retryCount + 1)
    }
    
    throw error
  }
}



/**
 * 创建加载提示
 */
export function createAnalysisLoadingModal() {
  const loadingHtml = `
    <div id="analysis-loading-modal" style="
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 10001;
    ">
      <div style="
        background: white;
        border-radius: 12px;
        padding: 30px;
        text-align: center;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        max-width: 400px;
        width: 90%;
      ">
        <div style="
          width: 50px;
          height: 50px;
          border: 4px solid #e3f2fd;
          border-top: 4px solid #2196f3;
          border-radius: 50%;
          margin: 0 auto 20px;
          animation: spin 1s linear infinite;
        "></div>
        <h3 style="
          margin: 0 0 10px 0;
          color: #2196f3;
          font-size: 18px;
          font-weight: bold;
        ">🤖 AI 正在分析论文...</h3>
        <p style="
          margin: 0;
          color: #666;
          font-size: 14px;
          line-height: 1.5;
        ">正在获取论文链接并进行智能分析<br/>请耐心等待1-2分钟</p>
        <div style="
          margin-top: 20px;
          padding: 12px;
          background: #f8f9fa;
          border-radius: 6px;
          border-left: 3px solid #2196f3;
          font-size: 12px;
          color: #666;
        ">
          💡 提示：分析完成后将在控制台显示结果
        </div>
      </div>
    </div>
  `

  // 添加CSS动画
  if (!document.getElementById('analysis-loading-styles')) {
    const styles = document.createElement('style')
    styles.id = 'analysis-loading-styles'
    styles.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `
    document.head.appendChild(styles)
  }

  document.body.insertAdjacentHTML('beforeend', loadingHtml)
}

/**
 * 移除加载提示
 */
export function removeAnalysisLoadingModal() {
  const loadingModal = document.getElementById('analysis-loading-modal')
  if (loadingModal) {
    loadingModal.style.opacity = '0'
    loadingModal.style.transform = 'scale(0.9)'
    setTimeout(() => {
      loadingModal.remove()
    }, 200)
  }
}

/**
 * 为选中的论文点添加高亮圈选效果
 * @param {string} paperId - 论文ID
 */
export function highlightPaperDot(paperId) {
  // 移除之前的高亮效果
  removeAllHighlights()
  
  // 查找对应的论文点
  const dots = document.querySelectorAll('.dot[data-paper-id]')
  dots.forEach(dot => {
    if (dot.dataset.paperId === paperId) {
      addHighlightEffect(dot)
    }
  })
}

/**
 * 移除所有高亮效果
 */
export function removeAllHighlights() {
  const highlightedDots = document.querySelectorAll('.dot.highlighted')
  highlightedDots.forEach(dot => {
    removeHighlightEffect(dot)
  })
}

/**
 * 为论文点添加高亮效果
 * @param {HTMLElement} dotElement - 论文点DOM元素
 */
function addHighlightEffect(dotElement) {
  dotElement.classList.add('highlighted')
  
  // 添加简单的高亮样式（如果还没有）
  if (!document.getElementById('paper-highlight-styles')) {
    const styles = document.createElement('style')
    styles.id = 'paper-highlight-styles'
    styles.textContent = `
      .dot.highlighted {
        background-color: #ff6b35 !important;
        transform: scale(1.1);
        transition: all 0.3s ease;
        z-index: 11;
      }
    `
    document.head.appendChild(styles)
  }
}

/**
 * 移除论文点的高亮效果
 * @param {HTMLElement} dotElement - 论文点DOM元素
 */
function removeHighlightEffect(dotElement) {
  dotElement.classList.remove('highlighted')
}
